import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetInstallabilityQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
export function usePollForInstallStatus({
  listingId,
  published = true,
  appContext
}) {
  var _data$installed;
  const [attemptCount, setAttemptCount] = useState(0);
  // useRef to avoid re-renders when storing interval ID
  const intervalRef = useRef(null);
  const maxAttempts = 20;
  const pollIntervalMs = 2000;
  const hasReachedMaxAttempts = attemptCount >= maxAttempts;
  const isPolling = attemptCount > 0 && !hasReachedMaxAttempts;
  const {
    data,
    loading,
    error,
    refetch
  } = useGetInstallabilityQuery({
    variables: {
      listingId: listingId,
      published
    },
    skip: !listingId || appContext !== 'app'
  });
  const installed = (_data$installed = data === null || data === void 0 ? void 0 : data.installed) !== null && _data$installed !== void 0 ? _data$installed : null;

  // Function to trigger a compatibility check
  const triggerCompatibilityCheck = useCallback(() => {
    if (!listingId) return;
    void refetch();
  }, [listingId, refetch]);

  // Start polling when we have a listingId
  useEffect(() => {
    if (!listingId || installed === true || hasReachedMaxAttempts) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Trigger initial check
    triggerCompatibilityCheck();
    intervalRef.current = setInterval(() => {
      setAttemptCount(prev => prev + 1);

      // Trigger compatibility check on each polling attempt
      triggerCompatibilityCheck();
    }, pollIntervalMs);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [listingId, installed, hasReachedMaxAttempts, triggerCompatibilityCheck]);

  // Stop polling when we reach max attempts
  useEffect(() => {
    if (hasReachedMaxAttempts && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [hasReachedMaxAttempts]);

  // Reset attempt count when listingId changes
  useEffect(() => {
    setAttemptCount(0);
  }, [listingId]);

  // Determine if there's an error
  const hasError = error || hasReachedMaxAttempts && installed !== true;
  return {
    installed,
    loading: loading || isPolling,
    error: hasError ? error || new Error('Installation polling reached maximum attempts without success') : null
  };
}